import { computed, inject, Injectable, Injector } from '@angular/core';

import { AdminModeService } from '../../auth/admin-mode/admin-mode.service';
import { Role, RoleGroup } from '../../auth/roles';
import { UserService } from '../../auth/user/user.service';
import { MaybeSignal, valueOf } from '../utils/signal/maybeSignal';

export interface UseAuthorizationParams {
  allowedRoles?: MaybeSignal<readonly Role[]>;
  skipWhitelistCheck?: MaybeSignal<boolean>;
}

export type UseAuthorization = ReturnType<AuthorizationComposable['use']>;

@Injectable({
  providedIn: 'root',
})
export class AuthorizationComposable {
  private readonly userService = inject(UserService);
  private readonly adminModeService = inject(AdminModeService);

  use({ allowedRoles = RoleGroup.CDF, skipWhitelistCheck = false }: UseAuthorizationParams = {}) {
    const loading = computed(() => !this.userService.doneLoading());

    const authorized = computed(() => {
      const user = this.userService.userSignal();

      return (
        !!user &&
        this.userService.userIsAllowed(valueOf(allowedRoles)) &&
        (valueOf(skipWhitelistCheck) || this.adminModeService.whitelistedOrInAdminMode())
      );
    });

    return { loading, authorized };
  }
}

export function useAuthorization({
  injector = inject(Injector),
  ...params
}: UseAuthorizationParams & { injector?: Injector } = {}) {
  return injector.get(AuthorizationComposable).use(params);
}
